///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { Loader } from "@inweb/viewer-core";
import { Viewer } from "../Viewer";
import { UpdateController, UpdateType } from "./UpdateController";

export class VSFXCloudStreamingLoader extends Loader {
  public viewer: Viewer;

  constructor(viewer: Viewer) {
    super();
    this.viewer = viewer;
  }

  override isSupport(file: any): boolean {
    return (
      typeof file === "object" &&
      typeof file.database === "string" &&
      typeof file.downloadResource === "function" &&
      /.vsfx$/i.test(file.database) &&
      this.viewer.options.enableStreamingMode === true &&
      this.viewer.options.enablePartialMode === false &&
      !/.rcs$/i.test(file.name)
    );
  }

  override async load(model: any): Promise<this> {
    if (!this.viewer.visualizeJs) return this;

    const visLib = this.viewer.visLib();
    const visViewer = visLib.getViewer();

    const updateController = new UpdateController();
    updateController.initialize(this.viewer);

    let isFireDatabaseChunk = false;

    const chunkLoadHandler = (progress: number, chunk: Uint8Array) => {
      if (!this.viewer.visualizeJs) return;

      let status;
      try {
        status = visViewer.parseVsfx(chunk);
      } catch (error: any) {
        console.error("VSFX parse error.", error);
        throw error;
      }

      updateController.update(UpdateType.kDelay);

      this.viewer.emitEvent({ type: "geometryprogress", data: progress, file: model.file, model });

      let state = false;
      if (
        status === visLib.DatabaseStreamStatus.ReadyServiceData ||
        (status === visLib.DatabaseStreamStatus.Complete && !isFireDatabaseChunk)
      ) {
        isFireDatabaseChunk = true;
        state = true;
      }

      if (state) {
        updateController.update(UpdateType.kForce);

        this.viewer.syncOpenCloudVisualStyle(false);
        this.viewer.syncOptions();
        this.viewer.syncOverlay();
        this.viewer.resize();

        this.viewer.emitEvent({ type: "databasechunk", data: chunk, file: model.file, model });
      } else {
        this.viewer.emitEvent({ type: "geometrychunk", data: chunk, file: model.file, model });
      }
    };

    console.time("File load time");

    await model.downloadResource(model.database, chunkLoadHandler, this.abortController.signal);
    updateController.update(UpdateType.kNormal);

    console.timeEnd("File load time");

    return Promise.resolve(this);
  }
}
